import React, { useState, useEffect, useRef } from "react";
import MetaTags from "react-meta-tags";
import { Container, Card, Col, Row, CardBody, Badge, Button, Spinner } from "reactstrap";
import { get, post } from 'helpers/api_helper';
import Datatables from "components/Datatables/datatable";
import moment from "moment";
import Toaster from "components/toster";

const MapConfig = () => {
    const [formData, setFormData] = useState({
        try_no: '',
        case_no: ''
    });
    const [isMapped, setIsMapped] = useState(false);
    const [checkingCase, setCheckingCase] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [dataRows, setDataRows] = useState([]);
    const [pagination, setPagination] = useState({
        limit: 10,
        page: 1,
        search: ''
    });

    const tryInputRef = useRef(null);

    const checkCaseMapping = async (caseVal) => {
        const trimmed = (caseVal || '').trim();
        if (!trimmed) {
            setIsMapped(false);
            return;
        }

        setCheckingCase(true);
        try {
            const res = await get(`/api/tryprocess/get_case_tray?case_no=${encodeURIComponent(trimmed)}`);
            if (res && res.is_mapped && res.data) {
                setIsMapped(true);
                setFormData(p => ({
                    ...p,
                    case_no: trimmed,
                    try_no: res.data.try_no || ''
                }));
            } else {
                setIsMapped(false);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setCheckingCase(false);
        }
    };

    const submitForm = async (e) => {
        if (e) e.preventDefault();
        const trimmedCase = (formData.case_no || '').trim();
        const trimmedTry = (formData.try_no || '').trim();

        if (!trimmedCase || trimmedCase.length < 4) {
            Toaster('warning', { message: 'Case / Order Number must be at least 4 characters.', title: 'Validation' });
            return;
        }
        if (!trimmedTry || trimmedTry.length < 4) {
            Toaster('warning', { message: 'Tray Number must be at least 4 digits or characters (e.g. 1234).', title: 'Validation' });
            return;
        }

        setSubmitting(true);
        try {
            const res = await post('/api/tryprocess/maptry', {
                case_no: trimmedCase,
                try_no: trimmedTry
            });

            if (res && res.status) {
                Toaster('success', {
                    message: res.msg || (isMapped ? 'Tray mapping updated successfully' : 'Tray mapped successfully'),
                    title: isMapped ? 'Tray Updated' : 'Success'
                });
                setFormData({
                    case_no: '',
                    try_no: ''
                });
                setIsMapped(false);
                fetchData();
            } else {
                Toaster('error', { message: res?.msg || 'Try Mapping Error', title: 'Error' });
            }
        } catch (err) {
            Toaster('error', { message: err?.message || 'Operation failed', title: 'Error' });
        } finally {
            setSubmitting(false);
        }
    };

    const fetchData = async () => {
        const { status, data } = await get('/api/tryprocess/getalltrys');
        if (status) {
            setDataRows(data);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEditRow = (row) => {
        setFormData({
            case_no: row.case_no || '',
            try_no: row.try_no || ''
        });
        setIsMapped(true);
        if (tryInputRef.current) {
            tryInputRef.current.focus();
        }
    };

    const datatableColumns = [
        {
            dataField: 'id',
            text: '#',
            formatter: (cell, row, rowIndex) => {
                const page = pagination?.page || 1;
                const limit = pagination?.limit || 10;
                return (page - 1) * limit + (rowIndex + 1);
            }
        },
        {
            dataField: 'try_no',
            text: 'Tray No',
            formatter: (cell) => <strong className="text-primary font-size-14"><i className="bx bx-shield-quarter me-1"></i>{cell}</strong>
        },
        {
            dataField: 'case_no',
            text: 'Case / Order No',
            formatter: (cell) => <span className="fw-semibold">{cell}</span>
        },
        {
            dataField: 'datetime',
            text: 'Added Date',
            formatter: (cell) => moment(cell).format('DD-MM-YYYY')
        },
        {
            dataField: 'time',
            text: 'Added Time',
            formatter: (cell, row) => moment(row.datetime).format('HH:mm:ss')
        },
        {
            dataField: 'actions',
            text: 'Action',
            isDummyField: true,
            formatter: (cell, row) => (
                <Button
                    color="primary"
                    size="sm"
                    outline
                    onClick={() => handleEditRow(row)}
                    title="Edit / Update Tray"
                >
                    <i className="bx bx-edit me-1"></i> Edit
                </Button>
            )
        }
    ];

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags><title>Try Map | Oralstop ERP</title></MetaTags>
                <Container fluid>
                    <h3>Try Map (Tray Assignment)</h3>
                </Container>
                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardBody>
                                <form onSubmit={submitForm}>
                                    <Row className="align-items-end">
                                        <Col md={3}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <label className="fw-semibold mb-0">Case / Order No *</label>
                                                {checkingCase ? (
                                                    <small className="text-muted"><Spinner size="sm" className="me-1" />checking...</small>
                                                ) : isMapped ? (
                                                    <Badge color="success" pill>Mapped</Badge>
                                                ) : null}
                                            </div>
                                            <input
                                                className="form-control"
                                                placeholder="Enter Case No (e.g. 2610000001)"
                                                value={formData?.case_no || ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setFormData(p => ({ ...p, case_no: val }));
                                                }}
                                                onBlur={(e) => checkCaseMapping(e.target.value)}
                                            />
                                        </Col>

                                        <Col md={3}>
                                            <label className="fw-semibold mb-1">Tray No *</label>
                                            <input
                                                ref={tryInputRef}
                                                className="form-control fw-bold"
                                                placeholder="Scan or enter Tray No (e.g. 0044)"
                                                value={formData?.try_no || ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setFormData(p => ({ ...p, try_no: val }));
                                                }}
                                            />
                                        </Col>

                                        <Col md={3} className="d-flex gap-2">
                                            <button
                                                className={`form-control btn btn-${isMapped ? 'success' : 'primary'}`}
                                                type="submit"
                                                disabled={submitting || !formData.case_no?.trim() || !formData.try_no?.trim()}
                                            >
                                                {submitting ? (
                                                    <><Spinner size="sm" className="me-1" /> Saving...</>
                                                ) : isMapped ? (
                                                    <><i className="bx bx-check-double me-1"></i> Update Tray</>
                                                ) : (
                                                    <><i className="bx bx-plus me-1"></i> Assign to Tray</>
                                                )}
                                            </button>
                                            {isMapped && (
                                                <button
                                                    className="btn btn-secondary"
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData({ case_no: '', try_no: '' });
                                                        setIsMapped(false);
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </Col>
                                    </Row>
                                </form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col xl="12">
                        <Card>
                            <CardBody>
                                <Datatables
                                    isSearch={true}
                                    columns={datatableColumns}
                                    showTableOnly={true}
                                    rowsLength={dataRows?.total_rows || 0}
                                    rows={dataRows?.rows || []}
                                    keyField={'id'}
                                    title="All Mapped Try's"
                                    loading={false}
                                    isAdd={false}
                                    isTableHead={true}
                                    ssr={setPagination}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </React.Fragment>
    );
};

export default MapConfig;
